import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Container, Row, Col,
  Spinner, Button, Label, FormGroup, FormText
} from 'reactstrap';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import { userGetRequest } from 'store/auth/action';
import { userUpdateRequest } from 'store/user/action';
import { studentsMetadataGetRequest } from 'store/student/action';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { createSelectStyles } from 'helpers/reactSelectLayout';

type UserSkill = { category: string; values: string[] };
type SkillCategory = { id: string; label: string; fields: string[] };

interface Props {
  readOnly?: boolean;
  userDetails?: any
}

const UserSkillsSetup: React.FC<Props> = ({ readOnly = false , userDetails}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const user = useSelector((s: ApplicationState) => s.auth.userProfile);
  const metaFields = useSelector((s: ApplicationState) => s.student.metaDataFields || []);
  const selectStyles = createSelectStyles('light');

  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(studentsMetadataGetRequest('students', { pageSize: 500 }));
  }, [dispatch]);

  useEffect(() => {
    const raw = user?.tenant?.skillSettings;
    if (raw) {
      try {
        const cfg = JSON.parse(raw);
        setCategories(cfg.categories || []);
      } catch {
        setCategories([]);
      }
    }

    try {
      const tId = user?.tenant?.id;
      const ut = userDetails?.userTenants?.find((x: { tenant_id: string; }) => x.tenant_id === tId);
      const initial = JSON.parse(ut?.skillSettings || '[]') || [];
      setSkills(initial);
    } catch {
      setSkills([]);
    }

    setLoading(false);
  }, [user]);

  const updateCategory = (category: string, selected: any[]) => {
    const selectedValues = selected.map((o) => o.value);
    setSkills((prev) => {
      const exists = prev.some((s) => s.category === category);
      return exists
        ? prev.map((s) =>
          s.category === category ? { ...s, values: selectedValues } : s
        )
        : [...prev, { category, values: selectedValues }];
    });
  };

  const saveSkills = () => {
    setSaving(true);
    dispatch(
      userUpdateRequest(
        user.id,
        {
          attributes: [
            {
              key: 'user_tenant_skill_settings',
              value: JSON.stringify(skills),
            },
          ],
        },
        () => {
          toast.success('Skills saved successfully.');
          dispatch(userGetRequest(user.id));
          setSaving(false);
        },
        () => {
          toast.error('Failed to save skills.');
          setSaving(false);
        }
      )
    );
  };

  const getSkillOptionsForCategory = (cat: SkillCategory) => {
    const optionsMap = new Map<string, { value: string; label: string }>();

    for (const field of cat.fields) {
      const meta = metaFields.find((m) => {
        const js = typeof m.valuesJson === 'string'
          ? JSON.parse(m.valuesJson)
          : m.valuesJson;
        return js?.key === field;
      });

      if (meta) {
        const js = typeof meta.valuesJson === 'string'
          ? JSON.parse(meta.valuesJson)
          : meta.valuesJson;

        const values = js?.values || js?.data?.values;

        if (Array.isArray(values)) {
          values.forEach((opt: any) => {
            if (opt?.value && opt?.label) {
              optionsMap.set(opt.value, { value: opt.value, label: opt.label });
            }
          });
        }
      }
    }

    return Array.from(optionsMap.values());
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner size="lg" />
      </Container>
    );
  }

  return (
      <Container fluid>
        <Card className="border-0 shadow-sm">
          <CardBody className="pb-4">
            <div className="mb-3 border-bottom pb-3">
              <h4 className="fw-bold">
                <i className="ri-user-settings-line me-2" />
                Your Skill Profile
              </h4>
              <p className="text-muted small mb-0">
                Help the system match you better by sharing your skills across different categories.
              </p>
            </div>

            {categories.length === 0 ? (
              <p className="text-muted">No skill categories defined by admin.</p>
            ) : (
              <Row className="gy-5">
                {categories.map((cat, index) => {
                  const selected = skills.find((s) => s.category === cat.label)?.values || [];
                  const options = getSkillOptionsForCategory(cat);
                  let selectedskills =  options.filter((o) => selected.includes(o.value))
                  return (
                    <Col md={6} key={cat.id}>
                      <div className="p-3 rounded h-100 border shadow-sm">
                        <FormGroup className="mb-0">
                          <Label className="fw-semibold fs-6 d-flex align-items-center mb-2">
                            <i className="ri-focus-2-line me-2 text-primary" />
                            {cat.label}
                          </Label>
                          {options.length === 0 ? (
                            <p className="text-muted small mt-2 mb-0">
                              No selectable skills available for this category.
                            </p>
                          ) : (
                            <>
                            {selectedskills?.length ?
                            <Select
                              isMulti
                              isDisabled={readOnly}
                              options={options}
                              styles={selectStyles}
                              value={options.filter((o) => selected.includes(o.value))}
                              onChange={(vals: any[]) => updateCategory(cat.label, vals)}
                              placeholder="Select skills…"
                              classNamePrefix="react-select"
                            />
                            : 
                            <div className="text-center p-2 rounded bg-light h-100">
                                        <i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
                                        <h6 className="fw-semibold text-muted">
                                            {/* {props.t("userpreferences.no_working_hours_set")}
                                             */}
                                             No skills set for this category.
                                        </h6>
                                    </div>
                           } </>
                          )}
                          {!readOnly ? <FormText color="muted">
                            Choose relevant skills that match your knowledge in this area.
                          </FormText>: null}
                        </FormGroup>
                      </div>
                    </Col>

                  );
                })}
              </Row>
            )}

            {!readOnly && categories.length > 0 && (
              <div className="text-end mt-5">
                <Button color="primary" onClick={saveSkills} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    'Save Skills'
                  )}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
  );
};

export default UserSkillsSetup;
